import { formatNumber } from 'cadenza/utils/format/format';
import { isGeographicalSrs } from 'cadenza/map/projection-utils';
import type { CoordinateDto } from 'cadenza/api-client/dto/coordinate-dto';

const DFL_PRECISION = 2;

/**
 * If this should be used according to some UX design, please ask the designer
 * why the design deviates from our default coordinate formatting and
 * un-deprecate this function is necessary.
 *
 * @param lat
 * @param lon
 * @deprecated Use formatCoordinate() instead
 */
export function formatLatLon (lat: number, lon: number) {
  return toStringHDMSIncludingMinAndSec([ lon, lat ]);
}

/**
 * Format a geographic coordinate with the hemisphere, degrees, minutes, and seconds.
 * inspired by ol/coordinate.js/toStringHDMS
 *
 * @param coordinate - Coordinate.
 * @return Hemisphere, degrees, minutes and seconds.
 */
function toStringHDMSIncludingMinAndSec (coordinate: CoordinateDto) {
  if (coordinate) {
    const ns = degreesToStringHDMSIncludingMinAndSec('NS', coordinate[1]);
    const ew = degreesToStringHDMSIncludingMinAndSec('EW', coordinate[0]);
    return (ns + '  ' + ew); // &nbsp;
  } else {
    return '';
  }
}

/**
 * The new OL version does not include min and sec if they are 0, we always want to include them.
 * inspired by ol/coordinate.js/degreesToStringHDMS
 *
 * @param hemispheres - Hemispheres.
 * @param degrees - Degrees.
 * @return String.
 */
function degreesToStringHDMSIncludingMinAndSec (hemispheres: string, degrees: number) {
  const normalizedDegrees = modulo(degrees + 180, 360) - 180;
  const x = Math.abs(3600 * normalizedDegrees);
  const precision = Math.pow(10, DFL_PRECISION);

  let deg = Math.floor(x / 3600);
  let min = Math.floor((x - deg * 3600) / 60);
  let sec = x - deg * 3600 - min * 60;
  sec = Math.ceil(sec * precision) / precision;

  if (sec >= 60) {
    sec = 0;
    min += 1;
  }

  if (min >= 60) {
    min = 0;
    deg += 1;
  }

  const paddedMinutes = formatNumber(min, {
    minimumIntegerDigits: 2
  });
  const paddedSecs = formatNumber(sec, {
    minimumIntegerDigits: 2,
    fractionDigits: DFL_PRECISION
  });
  const hem = normalizedDegrees === 0 ? '' : `${hemispheres.charAt(normalizedDegrees < 0 ? 1 : 0)} `;

  return `${hem}${deg}°${paddedMinutes}′${paddedSecs}″`;
}

/**
 * Formats coordinates as a string for display.
 *
 * The numbers are separated with an &ensp; space character and formatted with thousands separators.
 *
 * @param coords - An array of coordinate numbers
 * @param [srsCode] - A geographical SRS has x and y axes flipped and requires more fraction digits to be specific enough.
 * @return The formatted coordinate string
 */
export function formatCoordinate (coords: number[], srsCode?: string) {
  return formatCoordinateInternal(coords, srsCode, { useGrouping: true, separator: ' ' }); // &ensp;
}

/**
 * Formats coordinates as a string for copy/paste e.g. into Excel or the Gazetteer.
 *
 * The numbers are separated with a standard space character and formatted without thousands separators.
 *
 * @param coords - An array of coordinate numbers
 * @param [srsCode] - A geographical SRS has x and y axes flipped and requires more fraction digits to be specific enough.
 * @return The formatted coordinate string
 */
export function formatCoordinateForCopy (coords: number[], srsCode?: string) {
  return formatCoordinateInternal(coords, srsCode);
}

function formatCoordinateInternal (coords: number[], srsCode?: string, { useGrouping = false, separator = ' ' } = {}) {
  const isGeographical = (srsCode != null && isGeographicalSrs(srsCode));
  if (isGeographical) {
    coords = [ coords[1], coords[0] ];
  }
  return coords.map(coord => formatNumber(coord, { useGrouping, fractionDigits: isGeographical ? 7 : 2 })).join(separator);
}

/**
 * Returns the modulo of a / b, depending on the sign of b.
 *
 * @param a Dividend.
 * @param b Divisor.
 * @return Modulo.
 */
function modulo (a: number, b: number) {
  const r = a % b;
  return r * b < 0 ? r + b : r;
}
